import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import axios from "../../utils/api/axios";
import toast from "react-hot-toast";
import { MdVpnKey, MdCheckCircle, MdWarning, MdError, MdRefresh, MdClose } from "react-icons/md";

interface AdminCompanySSOTabProps {
  companyId: number;
}

const AdminCompanySSOTab: React.FC<AdminCompanySSOTabProps> = ({ companyId }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<{
    company?: { company_id: number; company_name: string; enforce_sso: boolean };
    connection?: {
      id: number;
      protocol: string;
      provider: string;
      display_name: string;
      oidc_issuer: string;
      oidc_client_id: string;
      status: string;
      Domains?: { id: number; domain: string; is_verified: boolean }[];
    } | null;
  }>({});
  const [killing, setKilling] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const fetchSSOData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/v2/sso/admin/company/${companyId}`);
      setData(res.data || {});
    } catch (err: any) {
      console.error("Failed to fetch admin SSO details:", err);
      toast.error(err?.response?.data?.message || "Failed to load SSO details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) {
      fetchSSOData();
    }
  }, [companyId]);

  const executeKillSwitch = async () => {
    setShowConfirmModal(false);
    setKilling(true);
    const toastId = toast.loading("Executing emergency kill-switch...");

    try {
      const res = await axios.post(`/v2/sso/admin/company/${companyId}/killswitch`);
      toast.success(res.data?.message || "Kill-switch executed successfully.", { id: toastId });
      fetchSSOData();
    } catch (err: any) {
      console.error("Kill-switch error:", err);
      toast.error(err?.response?.data?.message || "Failed to execute kill-switch.", { id: toastId });
    } finally {
      setKilling(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600"></div>
      </div>
    );
  }

  const connection = data.connection;
  const company = data.company;

  return (
    <div className="space-y-6 w-full">
      {/* Header */}
      <div className="flex items-center justify-between bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-slate-900 text-white rounded-lg">
            <MdVpnKey className="w-6 h-6" />
          </div>
          <div>
            <h3
              className="text-lg font-semibold text-gray-900"
              style={{ fontFamily: "Montserrat, sans-serif" }}
            >
              Enterprise Single Sign-On (SSO) Status
            </h3>
            <p className="text-xs text-gray-500">
              Read-only support & incident management view for CTB Staff Admins.
            </p>
          </div>
        </div>
        <button
          onClick={fetchSSOData}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
        >
          <MdRefresh className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {!connection ? (
        <div className="bg-white p-8 rounded-xl border border-gray-200 text-center space-y-2">
          <MdWarning className="w-10 h-10 mx-auto text-amber-500" />
          <h4 className="font-semibold text-gray-900">No SSO Connection Configured</h4>
          <p className="text-xs text-gray-500">
            This organization has not set up Single Sign-On yet.
          </p>
        </div>
      ) : (
        <>
          {/* Connection Details Card */}
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm space-y-4">
            <div className="flex items-center justify-between border-b border-gray-100 pb-4">
              <div>
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Provider Profile
                </span>
                <h4 className="text-base font-bold text-gray-900">{connection.display_name}</h4>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={`px-2.5 py-1 text-xs font-bold rounded-full uppercase ${
                    connection.status === "active"
                      ? "bg-green-100 text-green-700"
                      : connection.status === "disabled"
                      ? "bg-red-100 text-red-700"
                      : "bg-amber-100 text-amber-700"
                  }`}
                >
                  {connection.status}
                </span>
                {company?.enforce_sso && (
                  <span className="px-2.5 py-1 text-xs font-bold rounded-full bg-blue-100 text-blue-700 uppercase">
                    SSO Enforced
                  </span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
              <div>
                <span className="text-gray-500 font-medium">Protocol:</span>
                <p className="font-semibold text-gray-900 uppercase">{connection.protocol}</p>
              </div>
              <div>
                <span className="text-gray-500 font-medium">Provider Type:</span>
                <p className="font-semibold text-gray-900 capitalize">{connection.provider}</p>
              </div>
              <div>
                <span className="text-gray-500 font-medium">Okta Issuer URL:</span>
                <p className="font-mono text-gray-800 break-all">{connection.oidc_issuer || "N/A"}</p>
              </div>
              <div>
                <span className="text-gray-500 font-medium">Client ID:</span>
                <p className="font-mono text-gray-800 break-all">{connection.oidc_client_id || "N/A"}</p>
              </div>
            </div>
          </div>

          {/* Verified Domains Card */}
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm space-y-3">
            <h4 className="text-sm font-semibold text-gray-900">Registered Email Domains</h4>
            {!connection.Domains || connection.Domains.length === 0 ? (
              <p className="text-xs text-gray-500 italic">No email domains registered.</p>
            ) : (
              <div className="space-y-2">
                {connection.Domains.map((dom) => (
                  <div
                    key={dom.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 text-xs"
                  >
                    <span className="font-mono font-medium text-gray-900">@{dom.domain}</span>
                    {dom.is_verified ? (
                      <span className="flex items-center gap-1 text-xs text-green-700 font-semibold">
                        <MdCheckCircle className="w-4 h-4 text-green-600" /> Verified
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs text-amber-700 font-semibold">
                        <MdWarning className="w-4 h-4 text-amber-500" /> Unverified
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Incident Emergency Control */}
          {(() => {
            const canKill = connection?.status === "active" || company?.enforce_sso;
            let buttonText = "Execute Emergency Kill-Switch";
            if (killing) buttonText = "Executing Kill-Switch...";
            else if (connection?.status === "disabled") buttonText = "SSO Already Disabled (Kill-Switch Active)";
            else if (connection?.status === "draft") buttonText = "SSO Connection is in Draft";
            else if (!connection) buttonText = "No SSO Connection Configured";

            return (
              <div className={`p-6 rounded-xl border space-y-3 transition-colors ${canKill ? "bg-red-50 border-red-200" : "bg-gray-50 border-gray-200 opacity-80"}`}>
                <div className="flex items-center gap-2 text-red-800">
                  <MdError className={`w-5 h-5 ${canKill ? "text-red-600" : "text-gray-400"}`} />
                  <h4 className="font-bold text-sm text-gray-900">Emergency Support Kill-Switch</h4>
                </div>
                <p className="text-xs text-gray-600 leading-relaxed">
                  If the company's Okta server is down, broken, or misconfigured, use this button to immediately disable SSO and turn off mandatory password enforcement so their team can log in via password.
                </p>
                <button
                  type="button"
                  onClick={() => setShowConfirmModal(true)}
                  disabled={killing || !canKill}
                  className={`px-4 py-2 text-xs font-bold rounded-lg shadow-xs transition-colors ${
                    canKill
                      ? "text-white bg-red-600 hover:bg-red-700 cursor-pointer"
                      : "text-gray-500 bg-gray-200 cursor-not-allowed border border-gray-300"
                  }`}
                >
                  {buttonText}
                </button>
              </div>
            );
          })()}

          {/* Custom Confirmation Modal */}
          {showConfirmModal &&
            createPortal(
              <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/70 backdrop-blur-xs p-4">
                <div className="w-full max-w-md bg-white rounded-xl shadow-2xl border border-gray-200 p-6 space-y-5">
                  <div className="flex items-start justify-between border-b border-gray-100 pb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 bg-red-100 text-red-600 rounded-lg">
                        <MdError className="w-6 h-6" />
                      </div>
                      <div>
                        <h3
                          className="text-base font-bold text-gray-900"
                          style={{ fontFamily: "Montserrat, sans-serif" }}
                        >
                          Execute Emergency Kill-Switch?
                        </h3>
                        <p className="text-xs text-red-600 font-medium">
                          Incident Response Action
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowConfirmModal(false)}
                      className="text-gray-400 hover:text-gray-600 p-1 rounded-md transition-colors"
                    >
                      <MdClose className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="text-xs text-gray-700 space-y-3 leading-relaxed">
                    <p>
                      Are you sure you want to execute the emergency kill-switch for{" "}
                      <strong className="text-gray-900 font-bold">
                        {company?.company_name || `Company #${companyId}`}
                      </strong>?
                    </p>
                    <div className="p-3 bg-red-50 border border-red-200 text-red-800 rounded-lg text-[11px] font-medium space-y-1">
                      <p>⚠️ <strong>Immediate Effects:</strong></p>
                      <ul className="list-disc pl-4 space-y-0.5 text-red-700">
                        <li>SSO connection status will be changed to <strong>Disabled</strong>.</li>
                        <li>Mandatory SSO enforcement will be turned <strong>OFF</strong>.</li>
                        <li>All team members can log in using email & password immediately.</li>
                      </ul>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={() => setShowConfirmModal(false)}
                      className="px-4 py-2 text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={executeKillSwitch}
                      className="px-4 py-2 text-xs font-bold text-white bg-red-600 hover:bg-red-700 rounded-lg shadow-sm transition-colors"
                      style={{ fontFamily: "Montserrat, sans-serif" }}
                    >
                      Yes, Execute Kill-Switch
                    </button>
                  </div>
                </div>
              </div>,
              document.body
            )}
        </>
      )}
    </div>
  );
};

export default AdminCompanySSOTab;
